// Mock data for demo mode
import type { Appointment, Course, User } from '@workspace/api-zod';

export const mockCourses: Course[] = [
  {
    id: 1,
    title: 'Beginner Window Cleaning',
    description:
      'Perfect for those new to the industry. Learn fundamental techniques, safety protocols, and equipment handling.',
    level: 'beginner',
    price: 1299,
    duration: 5,
    maxStudents: 12,
    active: true,
    createdAt: new Date('2024-01-15').toISOString(),
    updatedAt: new Date('2024-01-15').toISOString(),
  },
  {
    id: 2,
    title: 'Intermediate Rope Access',
    description:
      'Advanced rope access techniques for high-rise buildings. IRATA Level 1 preparation included.',
    level: 'intermediate',
    price: 2499,
    duration: 12,
    maxStudents: 8,
    active: true,
    createdAt: new Date('2024-01-15').toISOString(),
    updatedAt: new Date('2024-01-15').toISOString(),
  },
  {
    id: 3,
    title: 'Advanced Master Technician',
    description:
      'Become a certified master technician. Includes swing stage operations, rescue procedures, and team leadership.',
    level: 'advanced',
    price: 3999,
    duration: 20,
    maxStudents: 6,
    active: true,
    createdAt: new Date('2024-01-15').toISOString(),
    updatedAt: new Date('2024-01-15').toISOString(),
  },
];

export const mockUser: User = {
  id: 1,
  name: 'Demo User',
  email: 'demo@example.com',
  phone: '555-0123',
  role: 'student',
  createdAt: new Date('2024-01-01').toISOString(),
  updatedAt: new Date('2024-01-01').toISOString(),
};

export const mockAdminUser: User = {
  id: 2,
  name: 'Admin Demo',
  email: 'admin@example.com',
  phone: '555-0124',
  role: 'admin',
  createdAt: new Date('2024-01-01').toISOString(),
  updatedAt: new Date('2024-01-01').toISOString(),
};

export const mockAppointments: Appointment[] = [
  {
    id: 1,
    userId: 1,
    courseId: 1,
    courseTitle: 'Beginner Window Cleaning',
    userName: 'Demo User',
    userEmail: 'demo@example.com',
    userPhone: '555-0123',
    scheduledAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'confirmed',
    price: 1299,
    notes: null,
    createdAt: new Date('2024-01-10').toISOString(),
    updatedAt: new Date('2024-01-10').toISOString(),
  },
  {
    id: 2,
    userId: 1,
    courseId: 2,
    courseTitle: 'Intermediate Rope Access',
    userName: 'Demo User',
    userEmail: 'demo@example.com',
    userPhone: '555-0123',
    scheduledAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    status: 'pending',
    price: 2499,
    notes: null,
    createdAt: new Date('2024-01-12').toISOString(),
    updatedAt: new Date('2024-01-12').toISOString(),
  },
];

export const mockUsers: User[] = [
  mockUser,
  mockAdminUser,
  {
    id: 3,
    name: 'John Smith',
    email: 'john@example.com',
    phone: '555-0125',
    role: 'student',
    createdAt: new Date('2024-01-05').toISOString(),
    updatedAt: new Date('2024-01-05').toISOString(),
  },
  {
    id: 4,
    name: 'Sarah Johnson',
    email: 'sarah@example.com',
    phone: '555-0126',
    role: 'student',
    createdAt: new Date('2024-01-08').toISOString(),
    updatedAt: new Date('2024-01-08').toISOString(),
  },
];

export const mockAnalytics = {
  summary: {
    totalUsers: 89,
    totalAppointments: 156,
    pendingAppointments: 23,
    confirmedAppointments: 45,
    completedAppointments: 78,
    cancelledAppointments: 10,
    totalRevenue: 45780,
    totalPageViews: 12450,
    totalCourses: 3,
    recentSignups: 12,
  },
  revenueByMonth: [
    { month: 'Jan 2024', revenue: 12500, appointments: 42 },
    { month: 'Feb 2024', revenue: 15200, appointments: 51 },
    { month: 'Mar 2024', revenue: 18080, appointments: 63 },
  ],
  appointmentsByStatus: [
    { status: 'confirmed', count: 45 },
    { status: 'pending', count: 23 },
    { status: 'completed', count: 78 },
    { status: 'cancelled', count: 10 },
  ],
  trafficByDay: [
    { date: '2024-03-15', views: 145 },
    { date: '2024-03-16', views: 167 },
    { date: '2024-03-17', views: 189 },
    { date: '2024-03-18', views: 156 },
    { date: '2024-03-19', views: 201 },
    { date: '2024-03-20', views: 178 },
    { date: '2024-03-21', views: 192 },
    { date: '2024-03-22', views: 210 },
    { date: '2024-03-23', views: 198 },
    { date: '2024-03-24', views: 185 },
    { date: '2024-03-25', views: 220 },
    { date: '2024-03-26', views: 205 },
    { date: '2024-03-27', views: 195 },
    { date: '2024-03-28', views: 230 },
    { date: '2024-03-29', views: 215 },
    { date: '2024-03-30', views: 240 },
    { date: '2024-03-31', views: 225 },
    { date: '2024-04-01', views: 235 },
    { date: '2024-04-02', views: 250 },
    { date: '2024-04-03', views: 245 },
    { date: '2024-04-04', views: 260 },
    { date: '2024-04-05', views: 255 },
    { date: '2024-04-06', views: 270 },
    { date: '2024-04-07', views: 265 },
    { date: '2024-04-08', views: 280 },
    { date: '2024-04-09', views: 275 },
    { date: '2024-04-10', views: 290 },
    { date: '2024-04-11', views: 285 },
    { date: '2024-04-12', views: 300 },
    { date: '2024-04-13', views: 295 },
  ],
};
